import { create } from 'zustand'
import { GameState, PlayerAction, ActionInfo } from '../types/game'

// アクション定義（クールダウン時間取得用）
const ACTIONS = [
  { id: PlayerAction.MAKE_RAIN, cooldownTime: 10 },
  { id: PlayerAction.TRIGGER_VOLCANO, cooldownTime: 30 },
  { id: PlayerAction.TRIGGER_EARTHQUAKE, cooldownTime: 25 },
  { id: PlayerAction.BOOST_LIFE, cooldownTime: 15 },
  { id: PlayerAction.MELT_ICE, cooldownTime: 20 },
  { id: PlayerAction.RAISE_LAND, cooldownTime: 45 },
  { id: PlayerAction.CREATE_STORM, cooldownTime: 15 },
  { id: PlayerAction.ADVANCE_TECH, cooldownTime: 35 },
  { id: PlayerAction.PURIFY_ENVIRONMENT, cooldownTime: 25 },
  { id: PlayerAction.GENERATE_RESOURCES, cooldownTime: 30 }
]
import { uiLogger } from '../utils/logger'

interface GameStore {
  // 状態
  worldId: string | null
  gameState: GameState | null
  playerEnergy: number
  cooldowns: Record<string, number>
  isLoading: boolean
  error: string | null
  selectedAction: PlayerAction | null
  lastLearningPoints: string[] | null
  lastActionName: string | null
  
  // アクション
  createWorld: (worldName?: string) => Promise<void>
  loadWorld: (worldId: string) => Promise<void>
  fetchGameState: (worldId: string) => Promise<void>
  executeAction: (action: PlayerAction) => Promise<void>
  tick: () => Promise<void>
  selectAction: (action: PlayerAction | null) => void
  
  // ヘルパー
  resetError: () => void
  updateCooldowns: () => void
  getLearningData: () => { points: string[], actionName: string } | null
  submitQuizAnswer: (worldId: string, isCorrect: boolean, category: string) => Promise<void>
}

const API_BASE = import.meta.env.DEV ? 'http://localhost:8789' : 'https://earth-simulator.matsumos.workers.dev'

export const useGameStore = create<GameStore>((set, get) => ({
  worldId: null,
  gameState: null,
  playerEnergy: 10000, // デバッグ用: 10000エネルギー
  cooldowns: {},
  isLoading: false,
  error: null,
  selectedAction: null,
  lastLearningPoints: null,
  lastActionName: null,
  
  createWorld: async (worldName = 'New Earth') => {
    set({ isLoading: true, error: null })
    
    try {
      const response = await fetch(`${API_BASE}/api/worlds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ worldName })
      })
      
      if (!response.ok) throw new Error('Failed to create world')
      
      const data = await response.json()
      set({
        worldId: data.data.worldId,
        gameState: data.data,
        isLoading: false
      })
      
      // 自動ティックを開始
      setInterval(() => get().tick(), 5000) // 5秒ごと
      
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false 
      })
    }
  },
  
  loadWorld: async (worldId: string) => {
    set({ isLoading: true, error: null })
    
    try {
      const response = await fetch(`${API_BASE}/api/worlds/${worldId}`)
      
      if (!response.ok) throw new Error('Failed to load world')
      
      const data = await response.json()
      set({
        worldId,
        gameState: data.data,
        playerEnergy: data.energy,
        cooldowns: data.cooldowns,
        isLoading: false
      })
      
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false 
      })
    }
  },
  
  fetchGameState: async (worldId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/worlds/${worldId}`)
      
      if (!response.ok) return
      
      const data = await response.json()
      
      // クールダウンのみを更新（他の状態は保持）
      set(state => ({
        cooldowns: data.cooldowns || {},
        playerEnergy: data.energy
      }))
    } catch (error) {
      // サイレントに失敗（定期更新なのでエラー表示しない）
      console.error('Failed to fetch game state:', error)
    }
  },
  
  executeAction: async (action: PlayerAction) => {
    const { worldId } = get()
    if (!worldId) return
    
    set({ isLoading: true, error: null })
    
    const startTime = performance.now()
    uiLogger.actionClicked(action, get().playerEnergy)
    
    try {
      const response = await fetch(`${API_BASE}/api/worlds/${worldId}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      })
      
      const duration = performance.now() - startTime
      uiLogger.apiCall(`/api/worlds/${worldId}/actions`, 'POST', duration)
      
      if (!response.ok) throw new Error('Failed to execute action')
      
      const data = await response.json()
      
      if (data.success) {
        set({
          gameState: data.state,
          playerEnergy: data.energy,
          cooldowns: data.cooldowns || {},
          isLoading: false
        })
        
        uiLogger.worldUpdated(data.state.worldAge, data.state.isAlive)
        
        // 学習ポイントを保存
        if (data.learningPoints && data.actionName) {
          set({
            lastLearningPoints: data.learningPoints,
            lastActionName: data.actionName
          })
        }
      } else {
        set({ 
          error: data.message,
          isLoading: false 
        })
      }
      
    } catch (error) {
      uiLogger.error(error as Error, { action })
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false 
      })
    }
  },
  
  tick: async () => {
    const { worldId } = get()
    if (!worldId) return
    
    try {
      const response = await fetch(`${API_BASE}/api/worlds/${worldId}/tick`, {
        method: 'POST'
      })
      
      if (!response.ok) return
      
      const data = await response.json()
      
      if (data.success) {
        set({
          gameState: data.state,
          playerEnergy: data.energy
        })
      }
      
    } catch (error) {
      console.error('Tick error:', error)
    }
  },
  
  selectAction: (action) => {
    set({ selectedAction: action })
  },
  
  resetError: () => {
    set({ error: null })
  },
  
  updateCooldowns: () => {
    // ローカルでのカウントダウンを削除
    // fetchGameStateで正確な値を取得するため
  },
  
  getLearningData: () => {
    const state = get()
    if (state.lastLearningPoints && state.lastActionName) {
      return {
        points: state.lastLearningPoints,
        actionName: state.lastActionName
      }
    }
    return null
  },
  
  submitQuizAnswer: async (worldId: string, isCorrect: boolean, category: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/worlds/${worldId}/quiz-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isCorrect, category })
      })
      
      if (!response.ok) return
      
      const data = await response.json()
      
      if (data.success) {
        set({
          gameState: data.state
        })
      }
    } catch (error) {
      console.error('Quiz answer submission failed:', error)
    }
  }
}))